// components/AppHeader.jsx
// =============================================================================
// GENEROSITY WTR APP - SINGLE HEADER COMPONENT
// One header for every surface. Replaces the four hand-rolled header patterns
// (shell header, floating glass hamburger, per-modal logo lockups, and the
// missing My Hydration header). The rule this component enforces:
//
//   Every screen has exactly one top bar. The top bar owns navigation.
//   The logo appears in only two places app-wide and NEITHER is here:
//     1. the drawer header (NavDrawer.jsx)
//     2. the Trust But Verify landing screen (kept as-is, founder gesture intact)
//   Every other screen is identified by its TITLE, not by re-stamping the logo.
//
// Three variants, one system:
//   variant="primary"  tabbed root screens (My Hydration, and any future root).
//                       Hamburger left, centered title, optional right status pill.
//   variant="hero"      full-bleed image screens (WTR BTL, WTR HUB).
//                       Hamburger left over a scrim, title on the same baseline,
//                       optional eyebrow (full product name) and status pill.
//   variant="modal"     drawer destinations (Profile, Intake, Health, Devices,
//                       Reports, Shop, Help, Auth). Title left, close X right.
//
// Palette is canon only: #FFFFFF / #51B0E6 / #A6A8AB / #F0F1F3.
// Type is canon only: Montserrat (title/eyebrow), Barlow Condensed via eyebrow.
// No new colors, no navy (#0F1419 drift retired here), no theme work.
// =============================================================================

const BRAND = {
  white: "#FFFFFF",
  blue: "#51B0E6",
  gray: "#A6A8AB",
  card: "#F0F1F3",
  ink: "#1A2535",     // body text (matches the modal screens' existing C.body)
  border: "rgba(26, 37, 53, 0.08)",
};

function MenuIcon({ color = BRAND.ink }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <line x1="4" y1="7" x2="20" y2="7" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
      <line x1="4" y1="17" x2="20" y2="17" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon({ color = BRAND.gray }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M6 6L18 18M18 6L6 18" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

// Round 38px control used for both menu and close, so the tap target is
// identical everywhere and builds muscle memory across screens.
function RoundButton({ onClick, ariaLabel, children, testId, onScrim = false, ...gesture }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        width: 38, height: 38, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0,
        background: onScrim ? "rgba(255,255,255,0.7)" : BRAND.card,
        border: `1px solid ${BRAND.border}`,
        backdropFilter: onScrim ? "blur(20px) saturate(180%)" : "none",
        WebkitBackdropFilter: onScrim ? "blur(20px) saturate(180%)" : "none",
        boxShadow: onScrim ? "0 2px 8px rgba(26,37,53,0.08)" : "none",
      }}
      {...gesture}
    >
      {children}
    </button>
  );
}

function StatusPill({ pill }) {
  if (!pill) return null;
  // pill: { label, tone } where tone is one of 'neutral'|'live'|'offline'|'score'
  const tones = {
    neutral: { bg: BRAND.card, fg: BRAND.ink, bd: BRAND.border },
    live:    { bg: "rgba(30,138,76,0.10)", fg: "#1E8A4C", bd: "rgba(30,138,76,0.20)" },
    offline: { bg: "rgba(166,168,171,0.14)", fg: "#6B7A8D", bd: BRAND.border },
    score:   { bg: "rgba(81,176,230,0.10)", fg: BRAND.blue, bd: "rgba(81,176,230,0.22)" },
  };
  const t = tones[pill.tone] || tones.neutral;
  return (
    <span
      data-testid="header-status-pill"
      style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
        padding: "5px 11px", borderRadius: 999,
        background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
        fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        fontFamily: "Montserrat, -apple-system, sans-serif",
      }}
    >
      {pill.label}
    </span>
  );
}

export default function AppHeader({
  variant = "primary",
  title,
  eyebrow = null,      // Barlow Condensed kicker above title (e.g. full product name)
  subtitle = null,     // muted line below title (modal variant)
  onMenu = null,       // renders the hamburger when provided
  onClose = null,      // renders the close X when provided (modal)
  pill = null,         // { label, tone }
  sticky = true,
}) {
  const wrapBase = {
    fontFamily: "Montserrat, -apple-system, sans-serif",
    zIndex: 100,
    boxSizing: "border-box",
  };

  // ---- HERO: over a full-bleed image; caller positions this absolutely at top.
  if (variant === "hero") {
    return (
      <div
        data-testid="app-header-hero"
        style={{
          ...wrapBase,
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "calc(env(safe-area-inset-top) + 12px) 16px 14px",
          display: "flex", alignItems: "flex-start", gap: 12,
          background: "linear-gradient(180deg, rgba(26,37,53,0.28) 0%, rgba(26,37,53,0) 100%)",
        }}
      >
        {onMenu && (
          <RoundButton onClick={onMenu} ariaLabel="Menu" testId="header-menu" onScrim>
            <MenuIcon color={BRAND.ink} />
          </RoundButton>
        )}
        <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          {eyebrow && (
            <div style={{
              fontFamily: "'Barlow Condensed', Montserrat, sans-serif",
              fontSize: 12, fontWeight: 600, letterSpacing: 1.5,
              textTransform: "uppercase", color: "rgba(255,255,255,0.85)",
              textShadow: "0 1px 6px rgba(26,37,53,0.5)", lineHeight: 1,
            }}>{eyebrow}</div>
          )}
          <div style={{
            fontSize: 22, fontWeight: 900, color: BRAND.white, letterSpacing: -0.5,
            lineHeight: 1.15, marginTop: eyebrow ? 3 : 0,
            textShadow: "0 1px 8px rgba(26,37,53,0.45)", whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
          }}>{title}</div>
        </div>
        {pill && <div style={{ paddingTop: 4 }}><StatusPill pill={pill} /></div>}
      </div>
    );
  }

  // ---- MODAL: drawer destinations. Title left, close right, no logo.
  if (variant === "modal") {
    return (
      <div
        data-testid="app-header-modal"
        style={{
          ...wrapBase,
          position: sticky ? "sticky" : "relative", top: 0,
          background: BRAND.white, borderBottom: `1px solid ${BRAND.border}`,
          padding: "16px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {onMenu && (
            <RoundButton onClick={onMenu} ariaLabel="Menu" testId="header-menu">
              <MenuIcon />
            </RoundButton>
          )}
          <div style={{ minWidth: 0 }}>
            {eyebrow && (
              <div style={{
                fontFamily: "'Barlow Condensed', Montserrat, sans-serif",
                fontSize: 11, fontWeight: 600, letterSpacing: 1.2,
                textTransform: "uppercase", color: BRAND.gray, lineHeight: 1,
              }}>{eyebrow}</div>
            )}
            <div style={{ fontSize: 20, fontWeight: 900, color: BRAND.ink, letterSpacing: -0.5, marginTop: eyebrow ? 2 : 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: 11, color: BRAND.gray, fontWeight: 600, marginTop: 1 }}>{subtitle}</div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {pill && <StatusPill pill={pill} />}
          {onClose && (
            <RoundButton onClick={onClose} ariaLabel="Close" testId="header-close">
              <CloseIcon />
            </RoundButton>
          )}
        </div>
      </div>
    );
  }

  // ---- PRIMARY (default): tabbed root screens. Hamburger left, centered title.
  return (
    <div
      data-testid="app-header-primary"
      style={{
        ...wrapBase,
        position: sticky ? "sticky" : "relative", top: 0,
        background: "rgba(255,255,255,0.85)",
        borderBottom: `1px solid ${BRAND.border}`,
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        padding: "10px 16px",
        display: "grid", gridTemplateColumns: "38px 1fr 38px", alignItems: "center", gap: 8,
      }}
    >
      <div style={{ justifySelf: "start" }}>
        {onMenu && (
          <RoundButton onClick={onMenu} ariaLabel="Menu" testId="header-menu">
            <MenuIcon />
          </RoundButton>
        )}
      </div>
      <div style={{ justifySelf: "center", textAlign: "center", minWidth: 0 }}>
        {eyebrow && (
          <div style={{
            fontFamily: "'Barlow Condensed', Montserrat, sans-serif",
            fontSize: 11, fontWeight: 600, letterSpacing: 1.4,
            textTransform: "uppercase", color: BRAND.gray, lineHeight: 1,
          }}>{eyebrow}</div>
        )}
        <div style={{ fontSize: 17, fontWeight: 900, color: BRAND.ink, letterSpacing: -0.3, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
      </div>
      <div style={{ justifySelf: "end" }}>
        {pill && <StatusPill pill={pill} />}
      </div>
    </div>
  );
}
